import React, { useState, useEffect } from 'react';
import { Shelf, Text, Icon, Button, TextLink, ButtonGroup, Token } from '@chaser/components';
import style from './VoiceCallingBanner.module.scss';
import TwilioService from '../../services/twilio-service';
import usePush from '../../hooks/usePush';
import useEvent from '../../hooks/useEvent';
import { Link } from 'react-router-dom';
import { CallStages } from '.';
import { BannerTypes, useBanner } from '../BannerControl';
import useShortCode from '../../hooks/useShortCode';
import cx from 'classnames';
import { useBannerContext } from '../BannerControl/BannerRenderer';
import TaskService from '../../services/task-service';
import { Features, TaskStatus, TaskTypes } from '../../util/constants';
import useUser from '../../hooks/useUser';
import CustomerService from '../../services/customer-service';
import { useCacheActions } from '../../hooks/useFetch';
import NumericKeypad from './NumericKeypad';
import { FeatureGate } from '../FeatureGate';

const TOTAL_ALLOWED_CALL_TIME_MINUTES = 20;
const WARNING_TIME_LEFT_MINUTES = 2;
const WARNING_AT_MINUTES = TOTAL_ALLOWED_CALL_TIME_MINUTES - WARNING_TIME_LEFT_MINUTES;

const CountdownButton = ({ countdownTimeMinutes = WARNING_TIME_LEFT_MINUTES }) => {
  const [countdown, setCountdown] = useState(countdownTimeMinutes * 60);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prevCountdown => {
        if (prevCountdown === 0) {
          clearInterval(timer);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const formatTime = time => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}m ${seconds < 10 ? '0' : ''}${seconds}s`;
  };

  return (
    <div className={style.countdown}>
      <a
        href="https://help.chaserhq.com/making-phone-calls-in-the-chaser-app"
        rel="noopener noreferrer"
        target="_blank"
      >
        Your call will end in {formatTime(countdown)}
      </a>
    </div>
  );
};

const UptickTimer = () => {
  const [time, setTime] = useState({
    sec: 0,
    min: 0,
  });

  const updateTimer = () => {
    setTime(prev => {
      let newTime = { ...prev };
      if (newTime.sec < 59) newTime.sec += 1;
      else {
        newTime.min += 1;
        newTime.sec = 0;
      }
      if (newTime.min === 60) {
        newTime.min = 0;
      }
      return newTime;
    });
  };

  const formatTime = time => {
    return `${time.min < 10 ? 0 : ''}${time.min}m ${time.sec < 10 ? 0 : ''}${time.sec}s`;
  };

  useEffect(() => {
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const showTimeWarning = time.min >= WARNING_AT_MINUTES;

  return (
    <Shelf gap="small">
      <Text color="White" fontWeight="bold">
        {formatTime(time)}
      </Text>
      {showTimeWarning && <CountdownButton />}
    </Shelf>
  );
};

const getRecordBorder = (isRecording, loading, hasRecorded) => {
  if (loading) return 'White';
  if (hasRecorded) return 'Green';
  if (isRecording) return 'Red';
  return 'Mid Neutral 1';
};

const getRecordingTitle = (isRecording, loading, hasRecorded) => {
  if (loading) return 'Loading...';
  if (hasRecorded) return 'Recording saved';
  if (isRecording) return 'Stop recording';
  return 'Start recording';
};

const ActionCallBanner = ({
  open,
  primaryContact = 'unknown',
  customerName = 'unknown',
  contactId,
}) => {
  const {
    opts: { callId },
  } = useBannerContext();
  const shortCode = useShortCode();
  const { invalidateEndpoint } = useCacheActions();
  const { userId } = useUser();
  const showBanner = useBanner();
  const [recordingId, setRecordingId] = useState(null);
  const { execute: createTask } = usePush(TaskService.createTask);
  const { execute: disconnectCall } = usePush(TwilioService.disconnectCall);
  const [hasRecorded, setHasRecorded] = useState(false);
  const { execute: executeRecording, loading: loadingRecording } = usePush(
    TwilioService.handleRecording,
  );
  const isRecording = !!recordingId;

  useEvent('disconnectCall', async args => {
    const [err, res] = await createTask({
      type: TaskTypes.CALL_REMINDER,
      assignee: userId,
      metadata: {
        contact: contactId,
        invoices: [],
        callId,
      },
      status: TaskStatus.COMPLETED,
      shortCode,
    });

    if (err) return;

    showBanner(BannerTypes.voiceCall, {
      ...args,
      callStage: CallStages.ended,
      recordingId,
      task: res,
    });

    await invalidateEndpoint(CustomerService.getCustomerHistory);
  });

  useEvent('cancelCall', disconnectCall);

  async function handleRecording() {
    if (hasRecorded) return;

    if (isRecording && !hasRecorded) {
      await executeRecording({
        stopRecording: true,
        shortCode,
        recordingSid: recordingId,
        callId,
      });

      setHasRecorded(true);
    } else {
      const [err, res] = await executeRecording({ startRecording: true, shortCode, callId });
      if (err) return;

      setRecordingId(res.recordingSid);
    }
  }

  if (!open) return null;

  return (
    <Shelf className={style.notificationBoxWrapper}>
      <Shelf className={style.notificationBox}>
        <Shelf justifyContent="space-between" width="100%" px="small">
          <Shelf className={style.columns__left}>
            <Icon name="phone" />
            <Text color="White" fontWeight="bold">
              Call in progress
            </Text>
            <Text color="White" fontWeight="bold">
              Call with {primaryContact} from{' '}
              <TextLink monochrome as={Link} to={`/customer/${contactId}`}>
                {customerName}
              </TextLink>
            </Text>
          </Shelf>
          <Shelf className={style.columns__right}>
            <Text color="White" fontWeight="bold">
              <UptickTimer />
            </Text>
            <ButtonGroup>
              <FeatureGate name={Features.CALL_RECORDING} upgradeType="locked">
                <div onClick={handleRecording} disabled={hasRecorded}>
                  <Token
                    color="White"
                    borderColor={getRecordBorder(isRecording, loadingRecording, hasRecorded)}
                    backgroundColor="transparent"
                    style={{
                      padding: '14px 16px',
                      cursor: hasRecorded ? 'not-allowed' : 'pointer',
                      width: '145px',
                    }}
                  >
                    <span
                      className={cx(
                        style.dot,
                        hasRecorded && style.hasRecorded,
                        isRecording && !loadingRecording && !hasRecorded && style.recording,
                        loadingRecording && style.loading,
                      )}
                    ></span>
                    {getRecordingTitle(isRecording, loadingRecording, hasRecorded)}
                  </Token>
                </div>
              </FeatureGate>
              <NumericKeypad />
              <Button intent="danger" onClick={disconnectCall}>
                End call
              </Button>
            </ButtonGroup>
          </Shelf>
        </Shelf>
      </Shelf>
    </Shelf>
  );
};

export default ActionCallBanner;
